<template>
    <modal @close="$emit('close')" ref="modal">
        <template v-slot:title>New Hire Sync (to POS) Options</template>

        <slot>
            <form-errors :errors="formErrors"/>

            <div class="form-row pb-2">
                <div class="col">
                    <form-input label="New Hire Sync to POS Enabled" v-model="formData.pos_new_hire_sync" :errors="errors.pos_new_hire_sync" type="checkbox" />
                </div>
            </div>

            <div class="form-row pb-2" v-if="formData.pos_new_hire_sync && ['synergy'].includes(integration.pos)">
                <div class="col">
                    <form-input label="Use API for New Hire Sync" v-model="formData.new_hires_use_api" :errors="errors.new_hires_use_api" type="checkbox" />
                </div>
            </div>

            <div class="form-row pb-2" v-if="formData.pos_new_hire_sync && ['crunchtime'].includes(integration.pos)">
                <div class="col">
                    <form-input label="Send SSN to POS" v-model="formData.pos_sync_ssn" :errors="errors.pos_sync_ssn" type="checkbox">
                        <template #popover-helper>
                            <p>
                                Write the employee's SSN into CrunchTime when a new hire is created or a terminated employee is rehired.
                            </p>
                            <p>
                                When off, new hires are created without an SSN and a rehire keeps whatever SSN is already on the CrunchTime record. SSNs already in CrunchTime are not removed.
                            </p>
                        </template>
                    </form-input>
                </div>
            </div>

            <div class="form-row pb-2" v-if="formData.pos_new_hire_sync && !['tcpplus'].includes(integration.pos)">
                <div class="col">
                    <form-input label="Default position (must exist in POS)" v-model="formData.pos_new_hire_default_position" :errors="errors.pos_new_hire_default_position" :nocaps="true" type="text">
                        <template #popover-helper>
                            <p>
                                For CaesarVision or Altametrics, default position to put new hires into (typically <b>Kitchen</b>, <b>Crew</b>, etc.)
                            </p>
                            <p>
                                Note: must be a valid position in CaesarVision/Altametrics for this client.
                            </p>
                        </template>
                    </form-input>
                </div>
            </div>

            <div class="form-row pb-2" v-if="formData.pos_new_hire_sync && ['tcpplus'].includes(integration.pos)">
                <div class="col">
                    <form-input label="Default Job Code (fallback)" v-model="formData.pos_new_hire_default_position" :errors="errors.pos_new_hire_default_position" :nocaps="true" type="text">
                        <template #popover-helper>
                            <p>
                                Default <b>jobCode</b> sent to TCPPlus for new hires whose Position has no <b>POS Value</b> set. Must be numeric and exist as a Job Code in TCPPlus.
                            </p>
                            <p>
                                Optional — falls back to <b>1000</b> if neither this nor the Position's POS Value is set.
                            </p>
                        </template>
                    </form-input>
                </div>
            </div>

            <div class="form-row pb-2" v-if="formData.pos_new_hire_sync">
                <div class="col">
                    <form-input label="Digest Notification Recipients (one per line)" v-model="formData.digest_notification_emails" :errors="errors.digest_notification_emails" :nocaps="true" type="textarea" :rows="4">
                        <template #popover-helper>
                            <p>
                                Recipients for <b>New Hire Sync Digest</b> notifications (leave blank to default to system admins)
                            </p>
                        </template>
                    </form-input>
                </div>
            </div>

            <div class="form-row pb-2" v-if="formData.pos_new_hire_sync">
                <div class="col">
                    <form-input label="Error Notification Recipients (one per line)" v-model="formData.error_notification_emails" :errors="errors.error_notification_emails" :nocaps="true" type="textarea" :rows="4">
                        <template #popover-helper>
                            <p>
                                Recipients for <b>New Hire Sync Error</b> notifications (leave blank to default to system admins)
                            </p>
                        </template>
                    </form-input>
                </div>
            </div>

        </slot>

        <template v-slot:footer>
            <div class="form-row">
                <div class="col">
                    <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Cancel</button>
                </div>
                <div class="col">
                    <button type="submit" class="btn btn-block btn-primary" @click.prevent="onSubmit()">Save</button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import api from '@/api'
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    mixins: [FormMixin, ModalMixin],
    props: ['client', 'integration', ],
    data() {
        return {
            dataLoaded: false,
            formData: this.makeFormData(),
        }
    },
    mounted() {

    },
    computed: {
        formRules() {
            let rules = {}

            if (this.formData.pos_new_hire_sync && !['tcpplus'].includes(this.integration.pos)) {
                rules['pos_new_hire_default_position'] = {presence: {allowEmpty: false}, }
            }

            if (this.formData.pos_new_hire_sync && ['tcpplus'].includes(this.integration.pos) && this.formData.pos_new_hire_default_position) {
                // Optional for tcpplus — backend falls back to Position.pos_value, then 1000.
                // But if set, must be numeric.
                rules['pos_new_hire_default_position'] = {
                    format: {pattern: /^\d+$/, message: 'must be numeric'},
                }
            }

            return rules
        },
    },
    methods: {
        makeFormData() {
            let fields = [
                'pos_new_hire_sync', 'pos_new_hire_default_position',
                'digest_notification_emails', 'error_notification_emails',
                'pos_sync_ssn',
            ]
            let formData = {}

            fields.forEach(f => {
                formData[f] = this.integration[f]
                if (['digest_notification_emails', 'error_notification_emails'].includes(f)) {
                    formData[f] = this.integration[f].join('\n')
                }
            })

            return formData
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            this.$store.dispatch('START_LOADING')
            let url = `/admin/clients/${this.client.id}/pos-integrations/${this.integration.id}/new-hire-pos-sync`
            api.put(url, this.formData).then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch((errors) => {
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
                this.$store.dispatch('STOP_LOADING')
            })
        },
    }
}
</script>
